import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicEventRegisterPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [its, setIts] = useState('');
  const [mobile, setMobile] = useState('');
  const [success, setSuccess] = useState(null); // { registration_id, card_url }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/public/events/${slug}`);
        if (!cancelled) setEvent(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Event not found or registration is closed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !mobile.trim()) {
      toast.error('Please fill name and mobile');
      return;
    }
    if (its && !/^\d{8}$/.test(its.trim())) {
      toast.error('ITS must be 8 digits');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/public/events/${slug}/register`, {
        name: name.trim(),
        its: its.trim(),
        mobile: mobile.trim(),
      });
      setSuccess(res.data);
      toast.success('Registered! ID card has been sent to your WhatsApp.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to load event</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    const cardUrl = `${process.env.REACT_APP_BACKEND_URL}${success.card_url}`;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 flex items-center justify-center">
        <Card className="max-w-2xl w-full shadow-xl">
          <CardContent className="p-6 sm:p-10 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="registration-success-heading">Thank you for registering!</h1>
            <p className="text-slate-600 mb-6">Your ID card has been sent to your WhatsApp number.</p>
            <div className="bg-white border rounded-xl overflow-hidden inline-block max-w-full">
              <img
                src={cardUrl}
                alt="Your ID card"
                className="max-w-full max-h-[60vh] object-contain"
                data-testid="registration-success-card"
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={cardUrl} download={`ID-Card-${name}.png`}>
                <Button data-testid="download-id-card-btn">Download ID Card</Button>
              </a>
              <Button
                variant="outline"
                onClick={() => { setSuccess(null); setName(''); setIts(''); setMobile(''); }}
                data-testid="register-another-btn"
              >
                Register Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 flex items-center justify-center">
      <Card className="max-w-md w-full shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl" data-testid="event-register-title">{event?.name}</CardTitle>
          {event?.event_date && (
            <p className="text-sm text-slate-500 mt-1">{new Date(event.event_date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          )}
          {event?.welcome_message && (
            <p className="text-slate-600 mt-3 text-sm whitespace-pre-line">{event.welcome_message}</p>
          )}
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reg-name">Full Name *</Label>
              <Input
                id="reg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
                data-testid="event-register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-its">ITS Number</Label>
              <Input
                id="reg-its"
                value={its}
                onChange={(e) => setIts(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="8-digit ITS (optional)"
                inputMode="numeric"
                data-testid="event-register-its-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-mobile">Mobile Number *</Label>
              <Input
                id="reg-mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="WhatsApp mobile number"
                inputMode="tel"
                required
                data-testid="event-register-mobile-input"
              />
              <p className="text-xs text-slate-500 mt-1">We'll send your ID card to this number on WhatsApp.</p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="event-register-submit-btn"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registering...</>) : 'Register'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
